import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// Create context
const AppSettingsContext = createContext();

// Hook to use the context
export const useAppSettings = () => {
  return useContext(AppSettingsContext);
};

// Default allowed statuses
const DEFAULT_ALLOWED_STATUSES = ['Normal', 'Defaulter'];

// Provider component
export const AppSettingsProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [allowedStatuses, setAllowedStatuses] = useState(DEFAULT_ALLOWED_STATUSES);
  const [loading, setLoading] = useState(true);

  // When the user changes or becomes authenticated, fetch the allowed statuses
  useEffect(() => {
    const fetchAllowedStatuses = async () => {
      if (!isAuthenticated || !user) {
        setAllowedStatuses(DEFAULT_ALLOWED_STATUSES);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // If the user already has association_allowed_status, use it
        if (user.association_allowed_status) {
          setAllowedStatuses(user.association_allowed_status);
          setLoading(false);
          return;
        }

        // Otherwise, if the user has an association, fetch the allowed statuses
        if (user.association_id) {
          // For non-admin users, fetch the association's allowed statuses
          try {
            const response = await api.get(`/api/admin/associations/${user.association_id}`);
            if (response.data && response.data.allowed_status) {
              setAllowedStatuses(response.data.allowed_status);
            } else {
              setAllowedStatuses(DEFAULT_ALLOWED_STATUSES);
            }
          } catch (error) {
            console.error("Error fetching association allowed statuses:", error);
            setAllowedStatuses(DEFAULT_ALLOWED_STATUSES);
          }
        } else {
          setAllowedStatuses(DEFAULT_ALLOWED_STATUSES);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchAllowedStatuses();
  }, [user, isAuthenticated]);

  // Provide context value
  const value = {
    allowedStatuses,
    setAllowedStatuses,
    loading
  };

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};